import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loading } from "@/components/shared";
import { Check, ChevronLeft, ChevronRight, Star, ShieldCheck, Lock, Clock } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const STEPS = ["Ratings", "Your Story", "iGaming Signals", "Disclosure & Submit"];
const RED_FLAGS = ["Delayed salary", "Payout reliability issues", "Micromanagement", "High turnover", "Unclear licensing", "Compliance concerns", "Toxic culture", "Poor work-life balance"];
const DEPARTMENTS = ["Engineering", "Product", "Marketing", "Compliance", "Payments", "Data", "Operations", "Other"];
const DISCLOSURE = [
  { key: "detailed", label: "Detailed", desc: "Show your exact role, hub and dates. Most useful to readers." },
  { key: "moderate", label: "Moderate", desc: "Show your department, hub and dates, but not your exact title." },
  { key: "minimal", label: "Minimal", desc: "Show only current or former employee and the dates." },
];

function StarInput({ label, value, onChange, testid }) {
  return (
    <div className="flex items-center justify-between rounded-xl border border-[#DDDAD1] p-4">
      <Label className="text-sm">{label}</Label>
      <div className="flex gap-1" data-testid={testid}>
        {[1, 2, 3, 4, 5].map((i) => (
          <button key={i} type="button" onClick={() => onChange(i)} aria-label={`${label} ${i} stars`}
            className="transition-transform hover:scale-110">
            <Star className={`h-6 w-6 ${i <= value ? "fill-amber-500 text-amber-500" : "text-slate-600"}`} />
          </button>
        ))}
      </div>
    </div>
  );
}

export default function SubmitReview() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [company, setCompany] = useState(null);
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [verify, setVerify] = useState(null);
  const draftKey = `review-draft-${id}`;

  const [form, setForm] = useState({
    rating_overall: 0, rating_culture: 0, rating_pay: 0, rating_management: 0,
    title: "", pros: "", cons: "", red_flags: [], job_title: "", employment_status: "Former",
    payout_reliability: 0, regulator_compliance: "Unknown", contract_type: "Full-time",
    remote_experience: "",
    salary_amount: "", salary_currency: "EUR", salary_period: "year",
    interview_difficulty: 0, interview_length: "", interview_experience: "", interview_outcome: "",
    disclosure_level: "moderate", department: "Operations", year_from: "", year_to: "",
  });

  useEffect(() => {
    api.get(`/companies/${id}`).then(({ data }) => setCompany(data)).catch(() => setCompany(false));
    const saved = localStorage.getItem(draftKey);
    if (saved) { try { setForm((f) => ({ ...f, ...JSON.parse(saved) })); toast.info("Draft restored"); } catch {} }
    // eslint-disable-next-line
  }, [id]);

  useEffect(() => {
    if (user) api.get("/verify/status", { params: { company_id: id } })
      .then(({ data }) => setVerify(data)).catch(() => setVerify({ status: "none" }));
    // eslint-disable-next-line
  }, [user, id]);

  // autosave
  useEffect(() => {
    const t = setTimeout(() => localStorage.setItem(draftKey, JSON.stringify(form)), 500);
    return () => clearTimeout(t);
  }, [form, draftKey]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleFlag = (f) => set("red_flags", form.red_flags.includes(f) ? form.red_flags.filter((x) => x !== f) : [...form.red_flags, f]);

  const canNext = () => {
    if (step === 0) return form.rating_overall > 0 && form.rating_culture > 0 && form.rating_pay > 0 && form.rating_management > 0;
    if (step === 1) return form.title.trim() && form.pros.trim() && form.cons.trim();
    return true;
  };

  const submit = async () => {
    if (!user) { toast.error("Please log in to submit a review."); nav("/login"); return; }
    setSubmitting(true);
    try {
      await api.post("/reviews", {
        company_id: id, ...form,
        payout_reliability: form.payout_reliability || null,
        salary_amount: form.salary_amount ? Number(form.salary_amount) : null,
        year_from: form.year_from ? Number(form.year_from) : null,
        year_to: form.year_to ? Number(form.year_to) : null,
        market: company?.market || "",
      });
      localStorage.removeItem(draftKey);
      toast.success("Thanks! Your review has been submitted.");
      nav("/dashboard");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (company === null) return <Loading />;
  if (company === false) return <Loading label="Company unavailable" />;

  // ---- Verification gate ----
  if (user === false) {
    return (
      <main className="mx-auto max-w-xl px-5 py-16 lg:px-8">
        <div data-testid="review-gate-login" className="card-surface p-8 text-center">
          <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-[#E7F0EE] text-teal-600"><Lock className="h-7 w-7" /></div>
          <h1 className="mt-4 text-2xl font-bold">Log in to review {company.name}</h1>
          <p className="mt-2 text-sm text-slate-400">You need an account so we can verify your employment and keep reviews trustworthy.</p>
          <button onClick={() => nav("/login")} className="btn-teal mt-6 text-sm">Log in or sign up</button>
        </div>
      </main>
    );
  }
  if (verify === null) return <Loading label="Checking your verification" />;
  if (verify.already_reviewed) {
    return (
      <main className="mx-auto max-w-xl px-5 py-16 lg:px-8">
        <div data-testid="review-gate-duplicate" className="card-surface p-8 text-center">
          <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-info-500/10 text-info-500"><Check className="h-7 w-7" /></div>
          <h1 className="mt-4 text-2xl font-bold">You've already reviewed {company.name}</h1>
          <p className="mt-2 text-sm text-slate-400">Each person can write one review per company. You can see its status on your dashboard.</p>
          <div className="mt-6 flex justify-center gap-3">
            <Link to="/dashboard" className="btn-teal text-sm">My dashboard</Link>
            <Link to={`/company/${id}`} className="inline-flex items-center rounded-[8px] border border-[#DDDAD1] px-6 py-3 text-sm text-slate-200 hover:border-teal-400/50">Back to profile</Link>
          </div>
        </div>
      </main>
    );
  }
  if (verify.status !== "verified") {
    return (
      <main className="mx-auto max-w-xl px-5 py-16 lg:px-8">
        <div data-testid="review-gate-verify" className="card-surface p-8 text-center">
          <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-[#E7F0EE] text-teal-600">
            {verify.status === "pending" ? <Clock className="h-7 w-7" /> : <ShieldCheck className="h-7 w-7" />}
          </div>
          <h1 className="mt-4 text-2xl font-bold">
            {verify.status === "pending" ? "Verification in review" : "Verify your employment first"}
          </h1>
          <p className="mt-2 text-sm text-slate-400">
            {verify.status === "pending"
              ? "A moderator is checking your evidence. You'll be able to publish once it's approved."
              : `Only verified employees can review ${company.name}. It takes about a minute.`}
          </p>
          {verify.status !== "pending" && (
            <button data-testid="go-verify" onClick={() => nav(`/company/${id}/verify`)} className="btn-teal mt-6 text-sm">Verify employment</button>
          )}
          {verify.status === "pending" && (
            <Link to="/dashboard" className="mt-6 inline-block text-sm text-teal-400 hover:underline">Go to your dashboard</Link>
          )}
        </div>
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-3xl px-5 py-10 lg:px-8">
      <p className="eyebrow mb-2 flex items-center gap-2"><ShieldCheck className="h-4 w-4" /> Verified reviewer</p>
      <h1 className="text-3xl font-bold">Reviewing {company.name}</h1>

      {/* Stepper */}
      <div className="mt-8 flex items-center">
        {STEPS.map((s, i) => (
          <div key={s} className="flex flex-1 items-center last:flex-none">
            <div className="flex flex-col items-center gap-2">
              <div data-testid={`step-${i}`} className={`grid h-9 w-9 place-items-center rounded-full border text-sm font-semibold transition-colors ${
                i < step ? "border-teal-400 bg-teal-400 text-white" : i === step ? "border-teal-400 text-teal-400" : "border-[#DDDAD1] text-slate-500"}`}>
                {i < step ? <Check className="h-4 w-4" /> : i + 1}
              </div>
              <span className={`hidden text-xs sm:block ${i === step ? "text-teal-400" : "text-slate-500"}`}>{s}</span>
            </div>
            {i < STEPS.length - 1 && <div className={`mx-2 h-0.5 flex-1 ${i < step ? "bg-teal-400" : "bg-black/[0.05]"}`} />}
          </div>
        ))}
      </div>

      <div className="card-surface mt-8 p-6 sm:p-8">
        {step === 0 && (
          <div className="space-y-4 animate-fade-up">
            <StarInput label="Overall" value={form.rating_overall} onChange={(v) => set("rating_overall", v)} testid="rate-overall" />
            <StarInput label="Culture & values" value={form.rating_culture} onChange={(v) => set("rating_culture", v)} testid="rate-culture" />
            <StarInput label="Pay & benefits" value={form.rating_pay} onChange={(v) => set("rating_pay", v)} testid="rate-pay" />
            <StarInput label="Management" value={form.rating_management} onChange={(v) => set("rating_management", v)} testid="rate-management" />
          </div>
        )}

        {step === 1 && (
          <div className="space-y-4 animate-fade-up">
            <div><Label>Headline</Label><Input data-testid="review-title" value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Summarise your experience" className="mt-1.5 border-[#DDDAD1] bg-ink-900" /></div>
            <div><Label className="text-success-500">Pros</Label><Textarea data-testid="review-pros" value={form.pros} onChange={(e) => set("pros", e.target.value)} placeholder="What was good?" className="mt-1.5 border-[#DDDAD1] bg-ink-900" /></div>
            <div><Label className="text-coral-500">Cons</Label><Textarea data-testid="review-cons" value={form.cons} onChange={(e) => set("cons", e.target.value)} placeholder="What could be better?" className="mt-1.5 border-[#DDDAD1] bg-ink-900" /></div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div><Label>Job title (optional)</Label><Input data-testid="review-job" value={form.job_title} onChange={(e) => set("job_title", e.target.value)} className="mt-1.5 border-[#DDDAD1] bg-ink-900" /></div>
              <div><Label>Employment status</Label>
                <Select value={form.employment_status} onValueChange={(v) => set("employment_status", v)}>
                  <SelectTrigger className="mt-1.5 border-[#DDDAD1] bg-ink-900"><SelectValue /></SelectTrigger>
                  <SelectContent className="border-[#DDDAD1] bg-ink-900">
                    <SelectItem value="Current">Current employee</SelectItem>
                    <SelectItem value="Former">Former employee</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-5 animate-fade-up">
            <StarInput label="Payout / salary reliability" value={form.payout_reliability} onChange={(v) => set("payout_reliability", v)} testid="rate-payout" />
            <div className="grid gap-4 sm:grid-cols-2">
              <div><Label>Regulator compliance</Label>
                <Select value={form.regulator_compliance} onValueChange={(v) => set("regulator_compliance", v)}>
                  <SelectTrigger className="mt-1.5 border-[#DDDAD1] bg-ink-900"><SelectValue /></SelectTrigger>
                  <SelectContent className="border-[#DDDAD1] bg-ink-900">
                    {["Compliant", "Non-compliant", "Unknown"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Contract type</Label>
                <Select value={form.contract_type} onValueChange={(v) => set("contract_type", v)}>
                  <SelectTrigger className="mt-1.5 border-[#DDDAD1] bg-ink-900"><SelectValue /></SelectTrigger>
                  <SelectContent className="border-[#DDDAD1] bg-ink-900">
                    {["Full-time", "Contract", "Affiliate", "Freelance"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Salary (optional, helps others)</Label>
              <div className="mt-1.5 flex gap-2">
                <Select value={form.salary_currency} onValueChange={(v) => set("salary_currency", v)}>
                  <SelectTrigger data-testid="salary-currency" className="w-24 border-[#DDDAD1] bg-ink-900"><SelectValue /></SelectTrigger>
                  <SelectContent className="border-[#DDDAD1] bg-ink-900">
                    {["EUR", "USD", "GBP"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input data-testid="salary-amount" type="number" min="0" value={form.salary_amount}
                  onChange={(e) => set("salary_amount", e.target.value)} placeholder="e.g. 60000" className="flex-1 border-[#DDDAD1] bg-ink-900" />
                <Select value={form.salary_period} onValueChange={(v) => set("salary_period", v)}>
                  <SelectTrigger data-testid="salary-period" className="w-28 border-[#DDDAD1] bg-ink-900"><SelectValue /></SelectTrigger>
                  <SelectContent className="border-[#DDDAD1] bg-ink-900">
                    <SelectItem value="year">per year</SelectItem>
                    <SelectItem value="month">per month</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="rounded-2xl border border-[#DDDAD1] p-4">
              <p className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-200">Interview experience <span className="text-xs font-normal text-slate-500">(optional)</span></p>
              <StarInput label="Interview difficulty" value={form.interview_difficulty} onChange={(v) => set("interview_difficulty", v)} testid="rate-interview" />
              <div className="mt-3 grid gap-3 sm:grid-cols-3">
                <Select value={form.interview_length} onValueChange={(v) => set("interview_length", v)}>
                  <SelectTrigger data-testid="interview-length" className="border-[#DDDAD1] bg-ink-900"><SelectValue placeholder="Length" /></SelectTrigger>
                  <SelectContent className="border-[#DDDAD1] bg-ink-900">
                    {["Under a week", "1-2 weeks", "2-4 weeks", "Over a month"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={form.interview_experience} onValueChange={(v) => set("interview_experience", v)}>
                  <SelectTrigger data-testid="interview-experience" className="border-[#DDDAD1] bg-ink-900"><SelectValue placeholder="Experience" /></SelectTrigger>
                  <SelectContent className="border-[#DDDAD1] bg-ink-900">
                    {["Positive", "Neutral", "Negative"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={form.interview_outcome} onValueChange={(v) => set("interview_outcome", v)}>
                  <SelectTrigger data-testid="interview-outcome" className="border-[#DDDAD1] bg-ink-900"><SelectValue placeholder="Outcome" /></SelectTrigger>
                  <SelectContent className="border-[#DDDAD1] bg-ink-900">
                    {["Offer", "Rejected", "Withdrew", "No response"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Red flags (select any that apply)</Label>
              <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
                {RED_FLAGS.map((f) => (
                  <label key={f} className="flex items-center gap-2 rounded-lg border border-[#DDDAD1] px-3 py-2 text-sm">
                    <Checkbox data-testid={`flag-${f}`} checked={form.red_flags.includes(f)} onCheckedChange={() => toggleFlag(f)} /> {f}
                  </label>
                ))}
              </div>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-5 animate-fade-up">
            <div className="flex items-center gap-2 rounded-xl border border-teal-400/30 bg-teal-500/5 p-4 text-sm text-teal-600">
              <ShieldCheck className="h-5 w-5" /> Employment verified{verify.verification?.method ? ` by ${verify.verification.method}` : ""}. Your review will carry a Verified employee badge.
            </div>

            <div>
              <Label>How much do you want to reveal about yourself?</Label>
              <p className="mt-1 text-xs text-slate-500">Your identity is never shown. This only controls the role details attached to your review.</p>
              <div className="mt-3 space-y-2" data-testid="disclosure-group">
                {DISCLOSURE.map((d) => {
                  const locked = d.key === "detailed" && !verify.detailed_allowed;
                  return (
                    <button key={d.key} type="button" disabled={locked} data-testid={`disclosure-${d.key}`}
                      onClick={() => set("disclosure_level", d.key)}
                      className={`flex w-full items-start gap-3 rounded-xl border p-4 text-left transition-colors ${form.disclosure_level === d.key ? "border-teal-400 bg-teal-500/10" : "border-[#DDDAD1] hover:border-teal-400/40"} ${locked ? "cursor-not-allowed opacity-50" : ""}`}>
                      <span className={`mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full border ${form.disclosure_level === d.key ? "border-teal-400 bg-teal-400 text-white" : "border-[#DDDAD1]"}`}>
                        {form.disclosure_level === d.key && <Check className="h-3 w-3" />}
                      </span>
                      <span>
                        <span className="flex items-center gap-2 text-sm font-semibold text-ink">{d.label}{locked && <Lock className="h-3.5 w-3.5 text-slate-400" />}</span>
                        <span className="block text-xs text-slate-500">{d.desc}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
              {!verify.detailed_allowed && (
                <p className="mt-2 text-xs text-slate-500">Detailed disclosure unlocks once at least 3 verified employees have reviewed this company. This protects small teams.</p>
              )}
            </div>

            <div className="grid gap-4 sm:grid-cols-3">
              <div><Label>Department</Label>
                <Select value={form.department} onValueChange={(v) => set("department", v)}>
                  <SelectTrigger data-testid="review-department" className="mt-1.5 border-[#DDDAD1] bg-ink-900"><SelectValue /></SelectTrigger>
                  <SelectContent className="border-[#DDDAD1] bg-ink-900">{DEPARTMENTS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Started (year)</Label><Input data-testid="review-year-from" type="number" min="2000" max="2026" value={form.year_from} onChange={(e) => set("year_from", e.target.value)} placeholder="2021" className="mt-1.5 border-[#DDDAD1] bg-ink-900" /></div>
              <div><Label>Ended (year)</Label><Input data-testid="review-year-to" type="number" min="2000" max="2026" value={form.year_to} onChange={(e) => set("year_to", e.target.value)} placeholder="2023" className="mt-1.5 border-[#DDDAD1] bg-ink-900" /></div>
            </div>

            <div className="rounded-xl border border-[#DDDAD1] p-5 text-sm text-slate-400">
              <p className="font-semibold text-slate-200">What happens next?</p>
              <p className="mt-2">Your review is anonymous. It goes through AI spam and abuse screening, then a human moderation queue. You'll see its status move from <span className="text-info-500">Under Review</span> to <span className="text-success-500">Published</span> (or <span className="text-coral-500">Declined</span> with a reason) on your dashboard.</p>
            </div>
          </div>
        )}

        <div className="mt-8 flex items-center justify-between">
          <button data-testid="step-back" onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0}
            className="inline-flex items-center gap-1 rounded-full border border-[#DDDAD1] px-5 py-2.5 text-sm text-slate-300 disabled:opacity-40">
            <ChevronLeft className="h-4 w-4" /> Back
          </button>
          {step < STEPS.length - 1 ? (
            <button data-testid="step-next" onClick={() => canNext() ? setStep((s) => s + 1) : toast.error("Please complete this step first")}
              className="btn-teal inline-flex items-center gap-1 text-sm">Next <ChevronRight className="h-4 w-4" /></button>
          ) : (
            <button data-testid="review-submit" onClick={submit} disabled={submitting} className="btn-teal text-sm disabled:opacity-50">
              {submitting ? "Submitting…" : "Submit review"}
            </button>
          )}
        </div>
      </div>
    </main>
  );
}
